// IndexedDB storage for the combat log analyzer.
//
// Runs in both the page and the worker, so it must not touch the DOM. Values go
// in as live objects (structured clone keeps Maps and Sets intact), which is why
// nothing here serializes to JSON.
//
// Stores
//   meta    - the directory handle and other small singletons
//   files   - one record per discovered log file: size, mtime, byte offset,
//             status and the file level summary
//   states  - resumable analyzer snapshots, so a growing file is only read from
//             its last processed byte
//   reports - one record per analysis segment, so the UI can load the whole log
//             overview without pulling every encounter

const DB_NAME = 'rio-combat-logs'
const DB_VERSION = 1

export const STORE_META = 'meta'
export const STORE_FILES = 'files'
export const STORE_STATES = 'states'
export const STORE_REPORTS = 'reports'

let dbPromise = null

function openDatabase() {
    if (dbPromise) return dbPromise

    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB is not available in this browser'))
            return
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION)

        request.onupgradeneeded = () => {
            const db = request.result
            if (!db.objectStoreNames.contains(STORE_META)) {
                db.createObjectStore(STORE_META)
            }
            if (!db.objectStoreNames.contains(STORE_FILES)) {
                const store = db.createObjectStore(STORE_FILES, { keyPath: 'id' })
                store.createIndex('directoryId', 'directoryId')
                store.createIndex('lastModified', 'lastModified')
            }
            if (!db.objectStoreNames.contains(STORE_STATES)) {
                db.createObjectStore(STORE_STATES)
            }
            if (!db.objectStoreNames.contains(STORE_REPORTS)) {
                const store = db.createObjectStore(STORE_REPORTS, { keyPath: 'id' })
                store.createIndex('fileId', 'fileId')
            }
        }

        request.onsuccess = () => {
            const db = request.result
            db.onversionchange = () => db.close()
            resolve(db)
        }
        request.onerror = () => reject(request.error || new Error('Unable to open the combat log database'))
        request.onblocked = () => reject(new Error('The combat log database is blocked by another tab'))
    })

    return dbPromise
}

function runRequest(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
    })
}

async function withStore(storeName, mode, handler) {
    const db = await openDatabase()
    return new Promise((resolve, reject) => {
        let result
        const transaction = db.transaction(storeName, mode)
        transaction.oncomplete = () => resolve(result)
        transaction.onerror = () => reject(transaction.error)
        transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'))
        Promise.resolve(handler(transaction.objectStore(storeName)))
            .then((value) => {
                result = value
            })
            .catch((error) => {
                try {
                    transaction.abort()
                } catch (abortError) {
                    // The transaction may already be finished; the rejection below is what matters.
                }
                reject(error)
            })
    })
}

export async function getMeta(key) {
    return withStore(STORE_META, 'readonly', (store) => runRequest(store.get(key)))
}

export async function setMeta(key, value) {
    return withStore(STORE_META, 'readwrite', (store) => runRequest(store.put(value, key)))
}

export async function deleteMeta(key) {
    return withStore(STORE_META, 'readwrite', (store) => runRequest(store.delete(key)))
}

export async function putFileRecord(record) {
    return withStore(STORE_FILES, 'readwrite', (store) => runRequest(store.put(record)))
}

export async function getFileRecord(id) {
    return withStore(STORE_FILES, 'readonly', (store) => runRequest(store.get(id)))
}

export async function listFileRecords(directoryId) {
    return withStore(STORE_FILES, 'readonly', (store) => {
        if (!directoryId) return runRequest(store.getAll())
        return runRequest(store.index('directoryId').getAll(directoryId))
    })
}

export async function deleteFileRecord(id) {
    await withStore(STORE_FILES, 'readwrite', (store) => runRequest(store.delete(id)))
    await deleteState(id)
    await deleteReports(id)
}

export async function putState(fileId, snapshot) {
    return withStore(STORE_STATES, 'readwrite', (store) => runRequest(store.put({
        fileId,
        savedAt: Date.now(),
        snapshot,
    }, fileId)))
}

export async function getState(fileId) {
    const record = await withStore(STORE_STATES, 'readonly', (store) => runRequest(store.get(fileId)))
    return record ? record.snapshot : null
}

export async function deleteState(fileId) {
    return withStore(STORE_STATES, 'readwrite', (store) => runRequest(store.delete(fileId)))
}

// Resume snapshots are the bulkiest records in the database, so only the most
// recently touched files keep one. Everything else can be re-analyzed.
export async function trimStates(keepFileIds, limit = 3) {
    const db = await openDatabase()
    const keys = await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_STATES, 'readonly')
        const store = transaction.objectStore(STORE_STATES)
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
    })

    const ordered = keys
        .filter((record) => !keepFileIds.includes(record.fileId))
        .sort((left, right) => right.savedAt - left.savedAt)

    const removable = ordered.slice(Math.max(0, limit - keepFileIds.length))
    await Promise.all(removable.map((record) => deleteState(record.fileId)))
    return removable.length
}

// Reports are the cache: they can always be rebuilt by reading the log again,
// so the store is capped and the least recently used file is evicted first.
// Serialized length is the yardstick; it is not the exact number of bytes
// IndexedDB writes, but it is proportional to it and costs one pass.
export const DEFAULT_REPORT_BUDGET_BYTES = 64 * 1024 * 1024

function estimateBytes(report) {
    try {
        return JSON.stringify(report).length
    } catch (error) {
        // A report that cannot be measured is still stored; it just does not
        // contribute to the budget, which only makes eviction more cautious.
        return 0
    }
}

export async function putReports(fileId, reports) {
    const now = Date.now()
    return withStore(STORE_REPORTS, 'readwrite', async (store) => {
        const index = store.index('fileId')
        const existing = await runRequest(index.getAllKeys(fileId))
        await Promise.all(existing.map((key) => runRequest(store.delete(key))))
        await Promise.all(reports.map((report) => runRequest(store.put({
            id: `${fileId}::${report.key}`,
            fileId,
            segmentKey: report.key,
            savedAt: now,
            usedAt: now,
            bytes: estimateBytes(report),
            report,
        }))))
        return reports.length
    })
}

export async function getReport(fileId, segmentKey) {
    const record = await withStore(STORE_REPORTS, 'readonly', (store) => runRequest(store.get(`${fileId}::${segmentKey}`)))
    if (record) touchReports(fileId)
    return record ? record.report : null
}

export async function listReports(fileId) {
    const records = await withStore(STORE_REPORTS, 'readonly', (store) => runRequest(store.index('fileId').getAll(fileId)))
    if (records && records.length) touchReports(fileId)
    return (records || []).map((record) => record.report)
}

// Marks a file's reports as freshly used. Nothing waits on it: a lost touch
// only means the file looks staler than it is the next time the cache is
// trimmed, and the report can be rebuilt anyway.
export function touchReports(fileId) {
    const now = Date.now()
    return withStore(STORE_REPORTS, 'readwrite', async (store) => {
        const index = store.index('fileId')
        const records = await runRequest(index.getAll(fileId))
        await Promise.all((records || []).map((record) => runRequest(store.put({ ...record, usedAt: now }))))
        return records ? records.length : 0
    }).catch(() => 0)
}

export async function listReportOwners() {
    const records = await withStore(STORE_REPORTS, 'readonly', (store) => runRequest(store.getAll()))
    return [...new Set((records || []).map((record) => record.fileId))]
}

export async function reportCacheStats() {
    const records = await withStore(STORE_REPORTS, 'readonly', (store) => runRequest(store.getAll()))
    const files = new Set()
    let bytes = 0
    ;(records || []).forEach((record) => {
        files.add(record.fileId)
        bytes += record.bytes || 0
    })
    return { files: files.size, reports: records ? records.length : 0, bytes }
}

// Drops whole files rather than single segments: half a file's reports would
// leave the view able to open one fight and not the next, which reads as a bug.
export async function trimReportCache(budgetBytes = DEFAULT_REPORT_BUDGET_BYTES, keepFileIds = []) {
    const records = await withStore(STORE_REPORTS, 'readonly', (store) => runRequest(store.getAll()))
    if (!records || !records.length) return { evicted: [], bytes: 0 }

    const byFile = new Map()
    let total = 0
    records.forEach((record) => {
        const entry = byFile.get(record.fileId) || { fileId: record.fileId, bytes: 0, usedAt: 0 }
        entry.bytes += record.bytes || 0
        entry.usedAt = Math.max(entry.usedAt, record.usedAt || record.savedAt || 0)
        byFile.set(record.fileId, entry)
        total += record.bytes || 0
    })

    if (total <= budgetBytes) return { evicted: [], bytes: total }

    const candidates = [...byFile.values()]
        .filter((entry) => !keepFileIds.includes(entry.fileId))
        .sort((left, right) => left.usedAt - right.usedAt)

    const evicted = []
    for (const entry of candidates) {
        if (total <= budgetBytes) break
        // eslint-disable-next-line no-await-in-loop
        await deleteReports(entry.fileId)
        total -= entry.bytes
        evicted.push(entry.fileId)
    }

    return { evicted, bytes: total }
}

export async function deleteReports(fileId) {
    return withStore(STORE_REPORTS, 'readwrite', async (store) => {
        const keys = await runRequest(store.index('fileId').getAllKeys(fileId))
        await Promise.all(keys.map((key) => runRequest(store.delete(key))))
        return keys.length
    })
}

export async function clearAll() {
    const db = await openDatabase()
    const stores = [STORE_META, STORE_FILES, STORE_STATES, STORE_REPORTS]
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(stores, 'readwrite')
        transaction.oncomplete = () => resolve(true)
        transaction.onerror = () => reject(transaction.error)
        stores.forEach((name) => transaction.objectStore(name).clear())
    })
}

export async function estimateUsage() {
    try {
        if (navigator && navigator.storage && navigator.storage.estimate) {
            const estimate = await navigator.storage.estimate()
            return { usage: estimate.usage || 0, quota: estimate.quota || 0 }
        }
    } catch (error) {
        // Storage estimates are best effort only.
    }
    return { usage: 0, quota: 0 }
}
